import {
    ApolloLink,
    Operation,
    NextLink,
    Observable,
    FetchResult,
} from '@apollo/client/core';

import { type IApplicationInsights } from '@microsoft/applicationinsights-web';
import { print } from 'graphql';
import { getOperationType } from '../utils/operations';

export default class ApplicationInsightsLink extends ApolloLink {
    constructor(public readonly appInsights: IApplicationInsights) {
        super();
    }

    public request(
        operation: Operation,
        forward?: NextLink
    ): Observable<FetchResult> {
        // Ensure link is used as middleware.
        if (!forward)
            throw new Error(
                'ApplicationInsightsLink cannot be used as an end-link.'
            );

        const start = Date.now();

        const operationType = getOperationType(operation);
        const operationProperties = {
            'graphql.document': print(operation.query),
            'graphql.operation.name': operation.operationName,
            'graphql.operation.type': operationType,
        };

        operation.setContext({
            appInsightsStart: start,
            appInsights: this.appInsights,
            headers: {
                traceparent: this.appInsights.context.telemetryTrace.traceID,
                ...(operation.getContext().headers ?? {}),
            },
        });

        return forward(operation).map((data) => {
            const context = operation.getContext();
            const start = context.appInsightsStart as number;
            const stop = Date.now();
            const time = stop - start;

            const properties = {
                'graphql.request.round_trip.start': start / 1000,
                'graphql.request.round_trip.stop': stop / 1000,
                'graphql.request.round_trip.ms': time,
                ...operationProperties,
            };

            try {
                this.appInsights.trackEvent({
                    name: `GraphQL ${operationType} '${operation.operationName}'`,
                    properties,
                });
            } catch (error) {
                console.error('Failed to send event to appInsights', error);
            }

            return data;
        });
    }
}
